import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { foyerApi } from "../../http/foyer";
import { ItemFormSurface } from "../../components/EditDrawer";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { describeError, useToast } from "../../components/toast";
import {
  STOREFRONT_ACCOUNT_KINDS,
  type StorefrontAccount,
  type StorefrontMember,
} from "@shared/storefront-customer";

/**
 * The supplier's own customers (`WL-3`).
 *
 * ## What this page is for
 *
 * A studio selling to hundreds of schools needs three answers on one screen:
 * who are my customers, who inside them can watch, and **what do they have
 * access to right now**. The third is the one nobody can look up anywhere else,
 * and it is derived from live grants rather than stored.
 *
 * ## ⚠️ The invitation token is shown ONCE
 *
 * It is a bearer credential — whoever holds it becomes that member. The server
 * stores only a hash, so this dialog is the only moment it exists in readable
 * form. The copy says so plainly rather than leaving somebody to discover it
 * when they close the dialog and go looking.
 *
 * ## Copy
 *
 * The audience is a commercial partner, not an operator. Empty states say what
 * happens next, never "no data".
 */
export default function SupplierCustomersPage() {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<StorefrontAccount[] | null>(null);
  const [members, setMembers] = useState<StorefrontMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<string, number>>({});
  const [newAccount, setNewAccount] = useState({ name: "", kind: "school" });
  const [invite, setInvite] = useState({ email: "", accountId: "" });
  const [issued, setIssued] = useState<{ email: string; token: string } | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  /**
   * The two create modals. Always mounted with a controlled `open`; their
   * fields are reset when the modal is OPENED, never on close (mid-fade).
   */
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  /** A refusal from a modal's own submit, shown inside that modal. */
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, people] = await Promise.all([
        foyerApi.storefrontAccounts(),
        foyerApi.storefrontMembers(),
      ]);
      setAccounts(list);
      setMembers(people);
      // One access read per organisation. A studio has hundreds of customers,
      // not hundreds of thousands, and the answer is the reason to be here.
      const counts: Record<string, number> = {};
      for (const account of list) {
        const held = await foyerApi.storefrontAccountAccess(account.id);
        counts[account.id] = held.assetIds.length;
      }
      setAccess(counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Resolves true once created, so the modal can close itself. */
  async function addAccount(): Promise<boolean> {
    setBusy(true);
    setFormError(null);
    try {
      await foyerApi.createStorefrontAccount(newAccount);
      toast.success(
        t("supplierCustomersPage.created", { name: newAccount.name })
      );
      await load();
      return true;
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
      toast.error(
        describeError(t, "createStorefrontAccount", e, {
          name: newAccount.name,
        })
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** The issued token on success (shown once, after the modal closes). */
  async function sendInvite(): Promise<{
    email: string;
    token: string;
  } | null> {
    setBusy(true);
    setFormError(null);
    try {
      const result = await foyerApi.inviteStorefrontMember({
        email: invite.email,
        // An empty selection means a PRIVATE customer — one person, one
        // licence, no organisation. That is a normal customer, not an edge.
        accountId: invite.accountId || null,
      });
      toast.success(
        t("supplierCustomersPage.inviteCreated", {
          email: result.member.email,
        })
      );
      await load();
      return { email: result.member.email, token: result.token };
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
      toast.error(
        describeError(t, "inviteStorefrontMember", e, { email: invite.email })
      );
      return null;
    } finally {
      setBusy(false);
    }
  }

  if (!accounts) return <CircularProgress size={24} />;

  const privateCustomers = members.filter((m) => !m.accountId);

  return (
    <Stack spacing={4}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Typography variant="h5" gutterBottom>
          {t("supplierCustomersPage.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("supplierCustomersPage.subtitle")}
        </Typography>

        <OwnerGate resource="storefront" level="edit">
          <Box sx={{ my: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setNewAccount({ name: "", kind: "school" });
                setFormError(null);
                setAddOpen(true);
              }}
              data-testid="customer-add"
            >
              {t("supplierCustomersPage.addCustomer")}
            </Button>
          </Box>
        </OwnerGate>

        <ItemFormSurface
          mode="create"
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title={t("supplierCustomersPage.addCustomer")}
          maxWidth="xs"
          busy={busy}
          footerTop={
            formError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {formError}
              </Alert>
            )
          }
          actions={(dismiss) => (
            <>
              <Button onClick={() => dismiss()} disabled={busy}>
                {t("common:buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={busy || !newAccount.name.trim()}
                onClick={() => void addAccount().then((ok) => ok && dismiss())}
              >
                {t("supplierCustomersPage.addCustomer")}
              </Button>
            </>
          )}
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label={t("supplierCustomersPage.organisation")}
              autoFocus
              fullWidth
              value={newAccount.name}
              onChange={(e) =>
                setNewAccount({ ...newAccount, name: e.target.value })
              }
            />
            <TextField
              size="small"
              select
              label={t("supplierCustomersPage.kind")}
              fullWidth
              value={newAccount.kind}
              onChange={(e) =>
                setNewAccount({ ...newAccount, kind: e.target.value })
              }
            >
              {STOREFRONT_ACCOUNT_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {t(`supplierCustomersPage.kinds.${kind}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </ItemFormSurface>

        {accounts.length === 0 ? (
          <Alert severity="info">{t("supplierCustomersPage.empty")}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("supplierCustomersPage.organisation")}</TableCell>
                <TableCell>{t("supplierCustomersPage.kind")}</TableCell>
                <TableCell>
                  {t("supplierCustomersPage.columns.people")}
                </TableCell>
                <TableCell>
                  {t("supplierCustomersPage.columns.titlesHeld")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow
                  key={account.id}
                  hover
                  onClick={() =>
                    navigate(
                      `/supplier/customers/${encodeURIComponent(account.id)}`
                    )
                  }
                  sx={{ cursor: "pointer" }}
                  data-testid={`customer-row-${account.id}`}
                >
                  <TableCell>
                    <RouterLink
                      to={`/supplier/customers/${encodeURIComponent(account.id)}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {account.name}
                    </RouterLink>
                    {!account.enabled && (
                      <Chip
                        size="small"
                        label={t("supplierCustomersPage.paused")}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {t(`supplierCustomersPage.kinds.${account.kind}`)}
                  </TableCell>
                  <TableCell>
                    {members.filter((m) => m.accountId === account.id).length}
                  </TableCell>
                  <TableCell>
                    {access[account.id] ?? t("common:labels.noValue")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          {t("supplierCustomersPage.inviteSomebody")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("supplierCustomersPage.inviteIntro")}
        </Typography>
        <OwnerGate resource="storefront" level="edit">
          <Box sx={{ my: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setInvite({ email: "", accountId: "" });
                setFormError(null);
                setInviteOpen(true);
              }}
              data-testid="customer-invite"
            >
              {t("supplierCustomersPage.inviteSomebody")}
            </Button>
          </Box>
        </OwnerGate>

        <ItemFormSurface
          mode="create"
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          title={t("supplierCustomersPage.inviteSomebody")}
          subtitle={t("supplierCustomersPage.inviteSubtitle")}
          maxWidth="xs"
          busy={busy}
          footerTop={
            formError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {formError}
              </Alert>
            )
          }
          actions={(dismiss) => (
            <>
              <Button onClick={() => dismiss()} disabled={busy}>
                {t("common:buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={busy || !invite.email.includes("@")}
                onClick={() =>
                  void sendInvite().then((result) => {
                    // The one-time token opens once the modal has gone.
                    if (result)
                      dismiss(() => {
                        setInviteOpen(false);
                        setIssued(result);
                      });
                  })
                }
              >
                {t("supplierCustomersPage.invite")}
              </Button>
            </>
          )}
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label={t("common:labels.email")}
              autoFocus
              fullWidth
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            />
            <TextField
              size="small"
              select
              label={t("supplierCustomersPage.organisation")}
              fullWidth
              value={invite.accountId}
              onChange={(e) =>
                setInvite({ ...invite, accountId: e.target.value })
              }
            >
              <MenuItem value="">
                {t("supplierCustomersPage.noOrganisation")}
              </MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </ItemFormSurface>

        {privateCustomers.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("supplierCustomersPage.privateCustomers", {
              count: privateCustomers.length,
            })}
          </Typography>
        )}
      </Box>

      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)}>
        <DialogTitle>
          {t("supplierCustomersPage.sendLink", { email: issued?.email })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("supplierCustomersPage.tokenOnce")}
          </Alert>
          <TextField
            fullWidth
            multiline
            value={issued?.token ?? ""}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssued(null)}>
            {t("common:buttons.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
